/**
 * Intelligence agent chat API (admin right-rail → Gemini → remote MCP).
 */

import { authenticatedHttpClient } from './httpClient';

export interface AgentChatMessage {
  role: 'user' | 'model' | 'assistant';
  content: string;
}

export interface SaleCreateLine {
  productId: string;
  code?: string;
  name?: string;
  price: number;
  quantity: number;
  discount?: number;
  taxRate?: number | null;
  taxId?: string | null;
  taxName?: string | null;
  warehouseId?: string | null;
}

/** Payload for action type `sale.create`. */
export interface SaleCreatePayload {
  clientId: string;
  clientName: string;
  paymentMethod: string;
  paymentCurrency?: string;
  products: SaleCreateLine[];
  creatorSalesPerson?: string;
  deliveryAddress?: string | null;
  comment?: string | null;
  subtotal?: number | null;
  ivaAmount?: number | null;
  total?: number | null;
  exchangeRate?: number | null;
  referenceCurrency?: string | null;
  currency?: string | null;
  exchangeSourceName?: string | null;
  orderDiscountPercent?: number | null;
}

/** Keep in sync with server ENABLED_ACTIONS / ACTION_SALE_CREATE. */
export const ACTION_SALE_CREATE = 'sale.create' as const;

export type AgentActionType = typeof ACTION_SALE_CREATE;

export interface SaleCreateAction {
  type: typeof ACTION_SALE_CREATE;
  status?: 'pending' | 'confirmed' | 'cancelled' | string;
  tool: 'create_sale' | string;
  payload: SaleCreatePayload;
  summary?: string | null;
}

/** Discriminated union — extend when new capabilities ship. */
export type AgentAction = SaleCreateAction;

export interface AgentResult {
  type: string;
  data?: Record<string, unknown>;
}

export interface AgentChatRequest {
  organizationId: string;
  message?: string;
  history?: AgentChatMessage[];
  confirmAction?: AgentAction | null;
}

export interface AgentChatResponse {
  reply: string;
  creditsCharged: number;
  creditsRemaining?: number | null;
  actions?: AgentAction[];
  results?: AgentResult[];
  timings?: Record<string, number> | null;
}

/** Compact "1234ms total · gemini_1 800ms · tool.search_products.1 200ms" */
export function formatAgentTimings(
  timings?: Record<string, number> | null
): string {
  if (!timings || Object.keys(timings).length === 0) return '';
  const total = timings.total;
  const rest = Object.entries(timings)
    .filter(([key]) => key !== 'total')
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([key, value]) => `${key} ${Math.round(value)}ms`);
  const head =
    typeof total === 'number' ? `${Math.round(total)}ms total` : '';
  return [head, ...rest].filter(Boolean).join(' · ');
}

export function isSaleCreateAction(action: AgentAction | null | undefined): action is SaleCreateAction {
  if (!action || action.type !== ACTION_SALE_CREATE) return false;
  const p = action.payload;
  return (
    !!p &&
    typeof p.clientId === 'string' &&
    typeof p.clientName === 'string' &&
    typeof p.paymentMethod === 'string' &&
    Array.isArray(p.products) &&
    p.products.length > 0
  );
}

export const sendAgentChat = async (
  request: AgentChatRequest
): Promise<AgentChatResponse> => {
  return authenticatedHttpClient.post<AgentChatResponse>(
    '/ai/agent/chat',
    {
      organizationId: request.organizationId,
      message: request.message || '',
      history: request.history || [],
      confirmAction: request.confirmAction || null,
    },
    { timeout: 120000 }
  );
};
